import { Fragment, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  KeyRound, RefreshCw, Send, Upload, Download, Copy, Check, X,
  ChevronDown, ChevronRight, History, Package, AlertTriangle, FileText,
} from 'lucide-react';
import {
  getLicenseStatus, getLicenseEntitlements, getLicenseHistory, getInstalledLicenseDetails,
  createLicenseActivationRequest, installLicense,
} from '../api/l1';
import Modal from '../components/Modal';
import { useAuth } from '../context/AuthContext';

// ── Real backend response shapes (license_routes.py) -- verified against source, not guessed ──

// Exported (RAM-215; consumer since updated to LicenseStatusBanner.tsx's "Get licensed" popover,
// see components/LicenseStatusBanner.tsx): embeds the two forms below directly rather than
// re-implementing activation-request/install, and needs this
// shape to type its own onInstalled callback.
export interface RealLicenseStatus {
  status: 'ACTIVE' | 'GRACE' | 'EXPIRED' | 'INVALID' | 'NOT_INSTALLED' | 'DEPLOYMENT_MISMATCH' | 'SUSPENDED' | 'RECOVERY';
  license_id: string;
  license_version: number;
  license_sequence: number;
  tenant_id: string;
  deployment_group_id: string;
  deployment_key_thumbprint: string;
  protection_mode: string;
  packages: string[];
  features: string[];
  environment_usage: { included_sandbox: number; licensed_customer_environments: number };
  not_before: string | null;
  expires_at: string | null;
  grace_until: string | null;
  verification_error_code: string | null;
}

// Pre-RAM-187 license_manager.status_dict() shape -- returned byte-identically whenever no real
// license has ever been installed (license_context.get_active_context() is None). Distinguished
// from RealLicenseStatus below by the presence of `license_id`, a field this shape never has.
interface StubLicenseStatus {
  status: string; // 'valid' | 'grace_period' | 'expired' | 'invalid'
  validation_mode: string;
  customer_id: string | null;
  license_key_present: boolean;
  expires_at: string | null;
  grace_period_ends_at: string | null;
  max_connections: number;
  active_connections: number;
  allowed_connectors: string[];
  features: Record<string, boolean>;
  message: string;
}

type LicenseStatusResponse = RealLicenseStatus | StubLicenseStatus;

function isRealLicenseStatus(data: LicenseStatusResponse): data is RealLicenseStatus {
  // Defensive, not just typed -- see licenseGateLogic.ts's own matching fix for why: a routing gap
  // can make this endpoint return a real 200 whose body isn't the expected object at all, and `in`
  // throws on a non-object.
  return typeof data === 'object' && data !== null && 'license_id' in data;
}

interface LicenseEntitlements {
  tenant_id: string;
  status: string;
  features: string[];
  packages: {
    package_id: string;
    environment_scope: string;
    monthly_api_execution_limit: number | null;
    protected_payload_key_id: string | null;
  }[];
  environment_entitlement: {
    included_bootstrap_sandbox_count: number;
    licensed_customer_environment_limit: number;
    minimum_production_environment_count: number;
  };
  limits: { max_active_api_principals: number | null };
}

interface LicenseEvent {
  event_id: string;
  license_row_id: string | null;
  license_sequence: number | null;
  tenant_id: string | null;
  deployment_group_id: string | null;
  event_type: string;
  actor_principal_id: string | null;
  package_id: string | null;
  environment_id: string | null;
  decision: string | null;
  reason_code: string | null;
  occurred_at: string | null;
  trace_id: string | null;
  details: Record<string, unknown>;
}

// deployment_identity.build_activation_request()'s exact return shape -- public key material,
// never a secret (the private key never leaves that module).
interface ActivationRequestResult {
  schema_version: string;
  activation_request_id: string;
  deployment_group_id: string;
  requested_tenant_id: string | null;
  runtime_version: string;
  deployment_public_key: Record<string, unknown>;
  deployment_key_thumbprint: string;
  supported_license_schema_versions: string[];
  supported_protection_modes: string[];
  generated_at: string;
  nonce: string;
}

interface InstallErrorDetail { code?: string; message: string; trace_id?: string }

// Direct user request, 2026-08-09: GET /license/installed's shape -- the full installed record
// (provenance + verbatim signed claims), distinct from RealLicenseStatus's live-computed summary
// above. `raw_claims` is the ORIGINAL signed payload exactly as issued (already verified at
// install time -- this is a read of the trusted, persisted record, not a re-verification).
interface InstalledLicenseDetails {
  license_id: string;
  license_version: number;
  license_sequence: number;
  tenant_id: string;
  deployment_group_id: string;
  status: string;
  installed_at: string | null;
  installed_by: string | null;
  activated_at: string | null;
  signature_key_id: string;
  payload_hash: string;
  raw_claims: Record<string, unknown>;
}

// ── Formatting helpers ──────────────────────────────────────────────────────

function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

function daysUntil(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return Math.ceil((d.getTime() - Date.now()) / 86_400_000);
}

function truncateMiddle(s: string, front = 14, back = 8): string {
  if (s.length <= front + back + 1) return s;
  return `${s.slice(0, front)}…${s.slice(-back)}`;
}

// ── Small shared display bits ───────────────────────────────────────────────

// Local status-pill component, matching the exact convention already established this session by
// ApiAccessUsers.tsx's StatusBadge / AuthzResources.tsx's SchemaStatusPill (a per-page `styles`
// map keyed by the domain's own exact status vocabulary) rather than the older, generically-keyed
// Badge.tsx elsewhere in this codebase -- LicenseState's values (ACTIVE/GRACE/...) don't overlap
// that component's existing MAP and this keeps the same look as every other RAM-18x page.
function LicenseStatusPill({ status }: { status: string }) {
  const styles: Record<string, string> = {
    ACTIVE: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    GRACE: 'bg-amber-50 text-amber-700 border-amber-200',
    EXPIRED: 'bg-red-50 text-red-600 border-red-200',
    INVALID: 'bg-red-50 text-red-600 border-red-200',
    SUSPENDED: 'bg-red-50 text-red-600 border-red-200',
    RECOVERY: 'bg-red-50 text-red-600 border-red-200',
    DEPLOYMENT_MISMATCH: 'bg-red-50 text-red-600 border-red-200',
    NOT_INSTALLED: 'bg-slate-100 text-slate-600 border-slate-200',
  };
  return (
    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${styles[status] || styles.NOT_INSTALLED}`}>
      {status.replace(/_/g, ' ')}
    </span>
  );
}

function DecisionPill({ decision }: { decision: string | null }) {
  if (!decision) return <span className="text-slate-300">—</span>;
  const styles: Record<string, string> = {
    installed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    generated: 'bg-blue-50 text-blue-700 border-blue-200',
    rejected: 'bg-red-50 text-red-600 border-red-200',
  };
  return (
    <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full border ${styles[decision] || 'bg-slate-100 text-slate-600 border-slate-200'}`}>
      {decision}
    </span>
  );
}

// Labeled row with a copy-to-clipboard affordance -- reused for every long identifier/hash in the
// status card (license_id, tenant_id, deployment_group_id, the key thumbprint). None of these are
// secrets, unlike ApiAccessUsers.tsx's CredentialModal -- copy is unrestricted, no "shown once".
function CopyRow({ label, value, truncate = false }: { label: string; value: string | null | undefined; truncate?: boolean }) {
  const [copied, setCopied] = useState(false);
  const shown = value ? (truncate ? truncateMiddle(value) : value) : '—';
  return (
    <div className="flex justify-between items-center border-b border-slate-100 pb-1.5 gap-3">
      <span className="text-slate-400 shrink-0">{label}</span>
      <span className="flex items-center gap-1.5 min-w-0">
        <span className="text-slate-700 font-medium font-mono text-xs truncate" title={value || undefined}>{shown}</span>
        {value && (
          <button
            type="button" className="btn-ghost p-1 rounded shrink-0" title="Copy"
            onClick={() => {
              navigator.clipboard?.writeText(value);
              setCopied(true);
              setTimeout(() => setCopied(false), 1200);
            }}
          >
            {copied ? <Check size={11} className="text-emerald-600" /> : <Copy size={11} />}
          </button>
        )}
      </span>
    </div>
  );
}

// ── Status cards ─────────────────────────────────────────────────────────────

function RichStatusCard({ data }: { data: RealLicenseStatus }) {
  const expiryDays = daysUntil(data.expires_at);
  const expiringSoon = expiryDays !== null && expiryDays >= 0 && expiryDays <= 14
    && data.status !== 'EXPIRED' && data.status !== 'INVALID';

  return (
    <div className="card p-5 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-slate-700">License Status</h2>
        <LicenseStatusPill status={data.status} />
      </div>

      {data.verification_error_code && (
        <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
          <strong>verification_error_code:</strong> <code>{data.verification_error_code}</code>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 text-sm">
        <CopyRow label="License ID" value={data.license_id} truncate />
        <div className="flex justify-between border-b border-slate-100 pb-1.5">
          <span className="text-slate-400">Version / Sequence</span>
          <span className="text-slate-700 font-medium font-mono text-xs">v{data.license_version} · seq {data.license_sequence}</span>
        </div>
        <CopyRow label="Tenant ID" value={data.tenant_id} />
        <CopyRow label="Deployment Group" value={data.deployment_group_id} truncate />
        <CopyRow label="Key Thumbprint" value={data.deployment_key_thumbprint} truncate />
        <div className="flex justify-between border-b border-slate-100 pb-1.5">
          <span className="text-slate-400">Protection Mode</span>
          <span className="text-slate-700 font-medium font-mono text-xs">{data.protection_mode}</span>
        </div>
      </div>

      <div>
        <div className="text-xs font-medium text-slate-600 mb-1.5">Features</div>
        {data.features.length === 0 ? (
          <div className="text-xs text-slate-400">No features granted.</div>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {data.features.map((f) => (
              <span key={f} className="text-[11px] font-medium px-2 py-0.5 rounded-full border bg-blue-50 text-blue-700 border-blue-200">{f}</span>
            ))}
          </div>
        )}
      </div>

      <div>
        <div className="text-xs font-medium text-slate-600 mb-1.5">Packages</div>
        {data.packages.length === 0 ? (
          <div className="text-xs text-slate-400">No packages entitled.</div>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {data.packages.map((p) => (
              <span key={p} className="text-[11px] font-medium px-2 py-0.5 rounded-full border bg-purple-50 text-purple-700 border-purple-200"><code>{p}</code></span>
            ))}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 text-sm">
        <div className="flex justify-between border-b border-slate-100 pb-1.5">
          <span className="text-slate-400">Included Sandbox Envs</span>
          <span className="text-slate-700 font-medium text-xs">{data.environment_usage.included_sandbox}</span>
        </div>
        <div className="flex justify-between border-b border-slate-100 pb-1.5">
          <span className="text-slate-400">Licensed Customer Envs</span>
          <span className="text-slate-700 font-medium text-xs">{data.environment_usage.licensed_customer_environments}</span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 text-sm">
        <div className="flex justify-between border-b border-slate-100 pb-1.5">
          <span className="text-slate-400">Not Before</span>
          <span className="text-slate-700 font-medium text-xs">{formatDateTime(data.not_before)}</span>
        </div>
        <div className="flex justify-between border-b border-slate-100 pb-1.5">
          <span className="text-slate-400">Expires At</span>
          <span className={`font-medium text-xs ${expiringSoon ? 'text-amber-600' : 'text-slate-700'}`}>
            {formatDateTime(data.expires_at)}
          </span>
        </div>
        <div className="flex justify-between border-b border-slate-100 pb-1.5">
          <span className="text-slate-400">Grace Until</span>
          <span className="text-slate-700 font-medium text-xs">{formatDateTime(data.grace_until)}</span>
        </div>
      </div>

      {expiringSoon && (
        <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex items-center gap-1.5">
          <AlertTriangle size={13} className="shrink-0" />
          This license expires in {expiryDays} day{expiryDays === 1 ? '' : 's'} -- renew before then to avoid an interruption.
        </div>
      )}
    </div>
  );
}

// Direct user request, 2026-08-09: "text form of the license... so the user knows what they are
// allowed as per license" -- an expandable section (collapsed by default, per the follow-up
// answer on placement), distinct from RichStatusCard's live-computed summary above. Query only
// fires once expanded (`enabled: open`) -- no reason to pay for the round trip on every page load
// when most visits won't open it.
function InstalledLicenseDetailsCard() {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const { data, isLoading, isError } = useQuery({
    queryKey: ['license-installed-details'],
    queryFn: () => getInstalledLicenseDetails().then((r) => r.data as InstalledLicenseDetails),
    enabled: open,
  });
  const claimsText = data ? JSON.stringify(data.raw_claims, null, 2) : '';

  return (
    <div className="card overflow-hidden">
      <button
        type="button"
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-50 text-left"
        onClick={() => setOpen((v) => !v)}
      >
        <span className="flex items-center gap-1.5 text-sm font-semibold text-slate-700">
          <FileText size={14} className="text-slate-400" /> Installed License Details
        </span>
        {open ? <ChevronDown size={14} className="text-slate-400" /> : <ChevronRight size={14} className="text-slate-400" />}
      </button>
      {open && (
        <div className="px-4 pb-4 pt-1 border-t border-slate-100 space-y-3">
          {isLoading ? (
            <div className="text-xs text-slate-400 py-3">Loading…</div>
          ) : isError || !data ? (
            <div className="text-xs text-red-600 py-3">Failed to load installed license details.</div>
          ) : (
            <>
              <div className="text-xs text-slate-500">
                The actual installed license record -- when and by whom it was installed, which
                signing key verified it, and the complete original signed grant exactly as issued
                by the Control Plane. Distinct from the live status above, which self-updates as
                time passes (e.g. ACTIVE → EXPIRED) -- this is a fixed, historical record of what
                was actually installed.
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 text-sm">
                <div className="flex justify-between border-b border-slate-100 pb-1.5">
                  <span className="text-slate-400">Installed At</span>
                  <span className="text-slate-700 font-medium text-xs">{formatDateTime(data.installed_at)}</span>
                </div>
                <div className="flex justify-between border-b border-slate-100 pb-1.5">
                  <span className="text-slate-400">Installed By</span>
                  <span className="text-slate-700 font-medium text-xs">{data.installed_by || '—'}</span>
                </div>
                <CopyRow label="Signing Key ID" value={data.signature_key_id} truncate />
                <CopyRow label="Payload Hash" value={data.payload_hash} truncate />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">
                    Full License Text (as signed by the Control Plane)
                  </label>
                  <button
                    type="button" className="btn-ghost text-xs flex items-center gap-1 px-2 py-1"
                    onClick={() => { navigator.clipboard?.writeText(claimsText); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
                  >
                    {copied ? <Check size={12} className="text-emerald-600" /> : <Copy size={12} />} Copy
                  </button>
                </div>
                <pre className="text-[11px] bg-slate-50 border border-slate-200 rounded-lg p-3 overflow-auto max-h-96 font-mono whitespace-pre-wrap break-all">{claimsText}</pre>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function EmptyLicenseCard({ data }: { data: StubLicenseStatus }) {
  return (
    <div className="card p-5 space-y-3">
      <div className="flex items-center gap-2.5">
        <div className="p-2 rounded-lg bg-slate-50 shrink-0"><KeyRound size={16} className="text-slate-400" /></div>
        <div>
          <h2 className="text-sm font-semibold text-slate-700">No License Installed</h2>
          <p className="text-xs text-slate-400">This deployment has never had a real signed license installed.</p>
        </div>
      </div>
      <p className="text-xs text-slate-500">
        Generate an activation request below and send it to the Control Plane to receive a signed
        license back, then install it here.
      </p>
      <div className="border-t border-slate-100 pt-3">
        <div className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-1.5">
          Legacy validation stub (pre-RAM-187)
        </div>
        <div className="space-y-1.5 text-xs">
          {[
            ['Status', data.status],
            ['Validation Mode', data.validation_mode],
            ['License Key Present', String(data.license_key_present)],
            ['Max Connections', String(data.max_connections)],
            ['Message', data.message || '—'],
          ].map(([label, val]) => (
            <div key={label} className="flex justify-between gap-4 border-b border-slate-50 pb-1">
              <span className="text-slate-400 shrink-0">{label}</span>
              <span className="text-slate-600 font-mono text-[11px] text-right truncate" title={val}>{val}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Actions: Generate Activation Request ────────────────────────────────────

// Exported (RAM-215) so LicenseStatusBanner.tsx's "Get licensed" popover can embed this directly
// instead of duplicating the activation-request flow. Behavior unchanged.
export function ActivationRequestForm({ onClose }: { onClose: () => void }) {
  const { user } = useAuth();
  // A deployment has exactly one tenant (RAM-176 single-tenant-per-license). When the signed-in
  // admin's session already establishes it (a real CIAM tenant login), use it -- never ask the
  // admin to type it. When it isn't known yet (a local-admin login, or a brand-new deployment
  // with no tenant fact anywhere yet), leave it unset and let the Control Plane assign it during
  // pairing (per the corrected issuance flow: the Tenant Admin uploads this request from their
  // own already-tenant-scoped Control Plane dashboard) -- never fall back to a free-text input.
  const tenant_id = user?.authSource === 'ciam' ? user?.tenantName : undefined;
  const [result, setResult] = useState<ActivationRequestResult | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');

  const mutation = useMutation({
    mutationFn: () => createLicenseActivationRequest(tenant_id || undefined).then((r) => r.data as ActivationRequestResult),
    onSuccess: (data) => { setError(''); setResult(data); },
    onError: (e: unknown) => {
      const detail = (e as { response?: { data?: { message?: string; detail?: string } } })?.response?.data;
      setError(detail?.message || detail?.detail || 'Failed to generate activation request');
    },
  });

  if (result) {
    const json = JSON.stringify(result, null, 2);
    const download = () => {
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${result.activation_request_id}.json`;
      a.click();
      URL.revokeObjectURL(url);
    };
    return (
      <div className="space-y-3">
        <div className="text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
          Not secret -- this is a public key plus identity descriptor, safe to copy or email to the
          Control Plane / license issuer. Send this to receive a signed license back.
        </div>
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Activation Request</label>
            <div className="flex gap-1.5">
              <button
                type="button" className="btn-ghost text-xs flex items-center gap-1 px-2 py-1"
                onClick={() => { navigator.clipboard?.writeText(json); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
              >
                {copied ? <Check size={12} className="text-emerald-600" /> : <Copy size={12} />} Copy
              </button>
              <button type="button" className="btn-ghost text-xs flex items-center gap-1 px-2 py-1" onClick={download}>
                <Download size={12} /> Download
              </button>
            </div>
          </div>
          <pre className="text-[11px] bg-slate-50 border border-slate-200 rounded-lg p-3 overflow-auto max-h-80 font-mono whitespace-pre-wrap break-all">{json}</pre>
        </div>
        <button className="btn-primary w-full" onClick={onClose}>Done</button>
      </div>
    );
  }

  return (
    <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); mutation.mutate(); }}>
      <div className="text-xs text-slate-500">
        Idempotently provisions (or reuses) this deployment's own keypair, then produces a
        descriptor to send to the Control Plane so it can issue a license bound to this deployment.
      </div>
      {tenant_id ? (
        <div>
          <label className="text-xs font-medium text-slate-600">Tenant</label>
          <input className="input mt-1 bg-slate-50 text-slate-500 cursor-not-allowed" value={tenant_id} readOnly disabled title="Already configured for your tenant" />
        </div>
      ) : (
        <div className="text-[11px] text-slate-400">
          Tenant isn't known yet -- the Control Plane assigns it when this request is paired.
        </div>
      )}
      {error && <div className="text-xs text-red-600">{error}</div>}
      <button className="btn-primary w-full" disabled={mutation.isPending}>
        {mutation.isPending ? 'Generating…' : 'Generate Activation Request'}
      </button>
    </form>
  );
}

// ── Actions: Install License ─────────────────────────────────────────────────

// Exported (RAM-215) so LicenseStatusBanner.tsx's "Get licensed" popover can embed this directly
// instead of duplicating the install flow. Behavior unchanged.
export function InstallLicenseForm({ onInstalled }: { onInstalled: (status: RealLicenseStatus) => void }) {
  const { user } = useAuth();
  const [license_jws, setLicenseJws] = useState('');
  const [installed_by, setInstalledBy] = useState(user?.username || '');
  const [fileName, setFileName] = useState('');
  // RAM-208: the trust bundle riding along in a downloaded `*.license.json` artifact (see
  // me-ui-staging's LicenseIssuance.tsx / CiamTenantDashboardPage.tsx download() functions) --
  // null when nothing was uploaded or the upload was an old-style bare-JWS file.
  const [trustedSigningKeys, setTrustedSigningKeys] = useState<Record<string, unknown> | null>(null);
  const [error, setError] = useState<InstallErrorDetail | null>(null);

  const mutation = useMutation({
    mutationFn: () => installLicense(license_jws.trim(), installed_by || undefined, trustedSigningKeys || undefined)
      .then((r) => r.data as RealLicenseStatus),
    onSuccess: (data) => { setError(null); onInstalled(data); },
    onError: (e: unknown) => {
      // Structured backend error -- install_license raises HTTPException(detail={"error": {code,
      // message, trace_id}}) on 422/403/409, but FastAPI's own exception handler wraps whatever
      // `detail` value is passed inside an OUTER "detail" key in the actual JSON response body, so
      // the real shape on the wire is {detail: {error: {code, message, trace_id}}} -- confirmed via
      // live verification (a real 409 LICENSE_VERSION_ROLLBACK came back as
      // {"detail":{"error":{"code":...}}}, not {"error":{...}} at the top level). Never a generic
      // "something went wrong": surface the real code+message the backend gives.
      const body = (e as { response?: { data?: { detail?: { error?: { code?: string; message?: string; trace_id?: string } } } } })?.response?.data;
      const err = body?.detail?.error;
      setError(err ? { code: err.code, message: err.message || 'Install failed', trace_id: err.trace_id } : { message: 'Install failed -- no error detail returned by the server.' });
    },
  });

  const onFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = () => {
      const raw = String(reader.result || '').trim();
      // RAM-208: a downloaded artifact is now JSON-wrapped ({schema, license_jws,
      // trusted_signing_keys}) so the trust bundle rides along in one file -- detect that shape
      // and unwrap it; fall back to treating the whole upload as a bare JWS (the only format
      // older downloads / hand-pasted files ever had) when it isn't valid JSON or doesn't look
      // like this wrapper.
      try {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object' && typeof parsed.license_jws === 'string') {
          setLicenseJws(parsed.license_jws.trim());
          setTrustedSigningKeys(parsed.trusted_signing_keys ?? null);
          return;
        }
      } catch {
        // Not JSON -- fall through to the bare-JWS path below.
      }
      setLicenseJws(raw);
      setTrustedSigningKeys(null);
    };
    reader.readAsText(file);
  };

  return (
    <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); mutation.mutate(); }}>
      <div className="text-xs text-slate-500">
        Paste the signed license (JWS) issued by the Control Plane in response to an activation
        request, or upload the file it was sent as.
      </div>
      <div>
        <label className="text-xs font-medium text-slate-600">License (JWS)</label>
        <textarea
          className="input mt-1 font-mono text-[11px]" rows={6} required
          value={license_jws}
          onChange={(e) => { setLicenseJws(e.target.value); setFileName(''); setTrustedSigningKeys(null); }}
          placeholder="eyJhbGciOi..."
        />
      </div>
      <div>
        <label className="btn-secondary text-xs inline-flex items-center gap-1.5 cursor-pointer">
          <Upload size={13} /> {fileName || 'Upload .license.json / .jws / .txt / .lic file'}
          <input type="file" accept=".json,.jws,.txt,.lic" className="hidden" onChange={onFile} />
        </label>
        {fileName && (
          <div className="text-[11px] text-slate-400 mt-1">
            {trustedSigningKeys
              ? 'Includes a trust bundle -- this deployment will learn to trust the Control Plane\'s current signing key(s) automatically.'
              : 'No trust bundle in this file -- this deployment must already trust (or separately learn) the key that signed it.'}
          </div>
        )}
      </div>
      <div>
        <label className="text-xs font-medium text-slate-600">Installed By (optional)</label>
        <input
          className="input mt-1" value={installed_by} onChange={(e) => setInstalledBy(e.target.value)}
          placeholder="admin identifier for the audit trail"
        />
      </div>
      {error && (
        <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2 space-y-1">
          {error.code && <div className="font-semibold"><code>{error.code}</code></div>}
          <div>{error.message}</div>
          {error.trace_id && <div className="text-[10px] text-red-400">trace_id: {error.trace_id}</div>}
        </div>
      )}
      <button className="btn-primary w-full" disabled={mutation.isPending || !license_jws.trim()}>
        {mutation.isPending ? 'Installing…' : 'Install License'}
      </button>
    </form>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function LicenseManagement() {
  const qc = useQueryClient();
  const [showActivation, setShowActivation] = useState(false);
  const [showInstall, setShowInstall] = useState(false);
  const [installBanner, setInstallBanner] = useState('');
  const [expandedEvent, setExpandedEvent] = useState<string | null>(null);

  const { data: statusData, isLoading: statusLoading, isFetching: statusFetching, refetch: refetchStatus } = useQuery({
    queryKey: ['license-status'],
    queryFn: () => getLicenseStatus().then((r) => r.data as LicenseStatusResponse),
  });
  const installed = !!statusData && isRealLicenseStatus(statusData);

  const { data: entitlements } = useQuery({
    queryKey: ['license-entitlements'],
    queryFn: () => getLicenseEntitlements().then((r) => r.data as LicenseEntitlements),
    // 402 LICENSE_NOT_INSTALLED whenever nothing is installed -- only fetch once the status card
    // itself has confirmed a real license exists, so this never surfaces a spurious query error.
    enabled: installed,
  });

  const { data: history, isLoading: historyLoading, refetch: refetchHistory } = useQuery({
    queryKey: ['license-history'],
    queryFn: () => getLicenseHistory(50).then((r) => r.data as { events: LicenseEvent[]; count: number }),
  });

  const invalidateAll = () => {
    qc.invalidateQueries({ queryKey: ['license-status'] });
    qc.invalidateQueries({ queryKey: ['license-entitlements'] });
    qc.invalidateQueries({ queryKey: ['license-history'] });
  };

  return (
    <div className="space-y-6 max-w-4xl">
      <Modal title="Generate Activation Request" open={showActivation} onClose={() => setShowActivation(false)}>
        <ActivationRequestForm onClose={() => setShowActivation(false)} />
      </Modal>
      <Modal title="Install License" open={showInstall} onClose={() => setShowInstall(false)}>
        <InstallLicenseForm
          onInstalled={(data) => {
            setShowInstall(false);
            setInstallBanner(`License installed -- status ${data.status}, sequence ${data.license_sequence}.`);
            invalidateAll();
          }}
        />
      </Modal>

      {/* User-directed fix (2026-08-10): "adjust the text to use the space on the right and
          buttons can be in the next row" -- same fix as the other page headers: title+description
          now take the full row width; the 3-button row (the widest of any page header here) moves
          below instead of relying on flex-wrap's implicit, less predictable fallback. */}
      <div className="space-y-3">
        <div className="flex items-start gap-3 min-w-0">
          <div className="p-2.5 rounded-xl bg-slate-50 shrink-0 mt-0.5">
            <KeyRound size={18} className="text-slate-500" />
          </div>
          <div className="min-w-0 flex-1">
            <h1 className="text-lg font-bold text-slate-900">License Management</h1>
            <p className="text-sm text-slate-500 mt-0.5">
              This deployment's signed runtime license -- entitlements, expiry, and the audit trail
              of every activation/install event.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => { refetchStatus(); refetchHistory(); }} disabled={statusFetching}
            className="btn-secondary flex items-center gap-1.5"
          >
            <RefreshCw size={13} className={statusFetching ? 'animate-spin' : ''} /> Refresh
          </button>
          <button onClick={() => setShowActivation(true)} className="btn-secondary flex items-center gap-1.5">
            <Send size={13} /> Generate Activation Request
          </button>
          <button onClick={() => setShowInstall(true)} className="btn-primary flex items-center gap-1.5">
            <Upload size={13} /> Install License
          </button>
        </div>
      </div>

      {installBanner && (
        <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 flex items-center justify-between">
          {installBanner}
          <button type="button" className="text-emerald-500 hover:text-emerald-700" onClick={() => setInstallBanner('')}><X size={13} /></button>
        </div>
      )}

      {statusLoading ? (
        <div className="card p-5 text-xs text-slate-400">Loading license status…</div>
      ) : !statusData ? (
        <div className="card p-5 text-xs text-red-600">Failed to load license status.</div>
      ) : installed ? (
        <RichStatusCard data={statusData as RealLicenseStatus} />
      ) : (
        <EmptyLicenseCard data={statusData as StubLicenseStatus} />
      )}

      {installed && <InstalledLicenseDetailsCard />}

      {installed && entitlements && entitlements.packages.length > 0 && (
        <div className="card p-5 space-y-3">
          <h2 className="text-sm font-semibold text-slate-700 flex items-center gap-1.5">
            <Package size={14} className="text-slate-400" /> Package Entitlements
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-slate-400 uppercase tracking-wide">
                <tr>
                  <th className="text-left font-semibold py-1.5 pr-3">Package</th>
                  <th className="text-left font-semibold py-1.5 pr-3">Environment Scope</th>
                  <th className="text-left font-semibold py-1.5 pr-3">Monthly API Limit</th>
                  <th className="text-left font-semibold py-1.5">Protected Payload Key</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {entitlements.packages.map((p) => (
                  <tr key={p.package_id}>
                    <td className="py-1.5 pr-3"><code>{p.package_id}</code></td>
                    <td className="py-1.5 pr-3 text-slate-500">{p.environment_scope}</td>
                    <td className="py-1.5 pr-3 text-slate-500">{p.monthly_api_execution_limit ?? 'unlimited'}</td>
                    <td className="py-1.5 text-slate-500">{p.protected_payload_key_id || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 text-xs pt-2 border-t border-slate-100">
            <div className="flex justify-between border-b border-slate-50 pb-1">
              <span className="text-slate-400">Min. Production Environments</span>
              <span className="text-slate-600 font-medium">{entitlements.environment_entitlement.minimum_production_environment_count}</span>
            </div>
            <div className="flex justify-between border-b border-slate-50 pb-1">
              <span className="text-slate-400">Max Active API Principals</span>
              <span className="text-slate-600 font-medium">{entitlements.limits.max_active_api_principals ?? 'unlimited'}</span>
            </div>
          </div>
        </div>
      )}

      <div className="card overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-1.5">
          <History size={14} className="text-slate-400" />
          <h2 className="text-sm font-semibold text-slate-700">License History</h2>
          <span className="text-xs text-slate-400 ml-1">{history?.count ?? 0} event{history?.count === 1 ? '' : 's'}</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
              <tr>
                <th className="w-8"></th>
                <th className="text-left px-4 py-2 font-semibold">Event</th>
                <th className="text-left px-4 py-2 font-semibold">Decision</th>
                <th className="text-left px-4 py-2 font-semibold">Reason</th>
                <th className="text-left px-4 py-2 font-semibold">Occurred</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {historyLoading ? (
                <tr><td colSpan={5} className="text-center text-slate-400 py-8">Loading…</td></tr>
              ) : (history?.events.length ?? 0) === 0 ? (
                <tr><td colSpan={5} className="text-center text-slate-400 py-8">No license events yet.</td></tr>
              ) : (
                history!.events.map((ev) => {
                  const isOpen = expandedEvent === ev.event_id;
                  return (
                    <Fragment key={ev.event_id}>
                      <tr className="hover:bg-slate-50 cursor-pointer" onClick={() => setExpandedEvent(isOpen ? null : ev.event_id)}>
                        <td className="px-4 py-2 text-slate-400">{isOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />}</td>
                        <td className="px-4 py-2 font-medium text-slate-900">{ev.event_type}</td>
                        <td className="px-4 py-2"><DecisionPill decision={ev.decision} /></td>
                        <td className="px-4 py-2 text-slate-500"><code className="text-xs">{ev.reason_code || '—'}</code></td>
                        <td className="px-4 py-2 text-slate-500">{formatDateTime(ev.occurred_at)}</td>
                      </tr>
                      {isOpen && (
                        <tr><td colSpan={5} className="p-0">
                          <div className="p-3 bg-slate-50 border-t border-slate-100 text-xs space-y-1">
                            <div><span className="text-slate-400">Event ID:</span> <code>{ev.event_id}</code></div>
                            {ev.license_sequence !== null && <div><span className="text-slate-400">Sequence:</span> {ev.license_sequence}</div>}
                            {ev.tenant_id && <div><span className="text-slate-400">Tenant:</span> {ev.tenant_id}</div>}
                            {ev.deployment_group_id && <div><span className="text-slate-400">Deployment Group:</span> <code>{ev.deployment_group_id}</code></div>}
                            {ev.actor_principal_id && <div><span className="text-slate-400">Actor:</span> {ev.actor_principal_id}</div>}
                            {ev.package_id && <div><span className="text-slate-400">Package:</span> {ev.package_id}</div>}
                            {ev.trace_id && <div><span className="text-slate-400">Trace ID:</span> <code>{ev.trace_id}</code></div>}
                            {ev.details && Object.keys(ev.details).length > 0 && (
                              <pre className="mt-1.5 text-[11px] bg-white border border-slate-200 rounded p-2 overflow-auto max-h-40 whitespace-pre-wrap break-all">{JSON.stringify(ev.details, null, 2)}</pre>
                            )}
                          </div>
                        </td></tr>
                      )}
                    </Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
